import {
  TextInput,
  NumberInput,
  Textarea,
  NativeSelect,
  Divider,
  Title,
  Alert,
  PasswordInput,
  Text,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { Signature } from './Signature';
import { MultiLevelCombobox } from './MultiLevelCombobox';
import { Products } from './ProductsInput';
import { InfiniteList } from './InfiniteList';
import { MapInput } from './MapInput';
import { ImagePicker } from './ImagePicker';
import { CheckboxGroup } from './CheckboxGroup';
import { RobeSizes } from './RobeSizes';
import { RadioButtonGroup } from './RadioButtonGroup';
import { useState } from 'react';
import { autoCorrectPhrase } from '@commons/autoCorrectPhrase';

const TextInputWithDiacritics = ({
  description,
  errorMessage,
  isRequired,
  label,
  name,
  onKeyDown,
  onChange,
  type,
  value,
  isDisabled,
  isReadonly,
  diacritics,
}: {
  description?: string;
  errorMessage?: string;
  isRequired?: boolean;
  label?: string;
  name: string;
  onKeyDown?: (value: any) => void;
  onChange?: (value: any) => void;
  type?: string;
  value?: string;
  isDisabled?: boolean;
  isReadonly?: boolean;
  diacritics?: string[];
}) => {
  const isDiacriticsEnabled =
    diacritics && diacritics.includes(name) && type === 'text';
  const [isLoadingDiacritics, setIsLoadingDiacritics] = useState(false);

  const handleBlur = async (event: React.FocusEvent<HTMLInputElement>) => {
    if (!isDiacriticsEnabled || isDisabled || isReadonly) return;

    const currentValue = event.currentTarget.value;
    if (!currentValue) return;

    // If value starts with "\", strip it and skip diacritics processing
    if (currentValue.startsWith('\\')) {
      onChange?.(currentValue.slice(1));
      return;
    }

    setIsLoadingDiacritics(true);

    try {
      const response =
        process.env.REACT_APP_SHOULD_ADD_DIACRITICS === 'true'
          ? await fetch(`${process.env.REACT_APP_DIACRITICS_API}/lookup`, {
              method: 'POST',
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ name: currentValue }),
            })
          : {
              json: async () => ({ name: currentValue }),
            };

      const data = await response.json();
      onChange?.(data.name);
    } catch (error) {
      console.error('Error applying diacritics:', error);
    } finally {
      setIsLoadingDiacritics(false);
    }
  };

  return (
    <TextInput
      description={description}
      error={errorMessage}
      required={isRequired}
      label={label}
      name={name}
      onKeyDown={onKeyDown}
      onChange={(event) => {
        onChange?.(event.currentTarget.value);
      }}
      onBlur={handleBlur}
      type={type}
      value={value}
      disabled={isDisabled || isLoadingDiacritics}
      readOnly={isReadonly}
    />
  );
};

const TextareaWithAutoCorrect = ({
  description,
  errorMessage,
  isRequired,
  label,
  name,
  onChange,
  value,
  isDisabled,
  isReadonly,
  autoCorrect,
}: {
  description?: string;
  errorMessage?: string;
  isRequired?: boolean;
  label?: string;
  name: string;
  onChange?: (value: any) => void;
  value?: string;
  isDisabled?: boolean;
  isReadonly?: boolean;
  autoCorrect?: string[];
}) => {
  const isAutoCorrectEnabled = autoCorrect && autoCorrect.includes(name);

  const handleBlur = (event: React.FocusEvent<HTMLTextAreaElement>) => {
    if (!isAutoCorrectEnabled || isDisabled || isReadonly) return;

    const currentValue = event.currentTarget.value;
    if (!currentValue) return;

    const correctedValue = autoCorrectPhrase(currentValue);
    if (correctedValue !== currentValue) {
      onChange?.(correctedValue);
    }
  };

  return (
    <Textarea
      label={label}
      minRows={6}
      maxRows={18}
      autosize={true}
      name={name}
      description={description}
      value={value}
      onChange={(event) => {
        onChange?.(event.currentTarget.value);
      }}
      onBlur={handleBlur}
      disabled={isDisabled || isReadonly}
    />
  );
};

const getVariantColor = (variant: string) => {
  switch (variant) {
    case 'informative':
      return 'blue';
    case 'negative':
      return 'red';
    case 'neutral':
      return 'gray';
    case 'notice':
      return 'yellow';
    case 'positive':
      return 'green';
    default:
      return 'blue';
  }
};

const getMinDate = (dateMinValue: string | undefined) => {
  if (dateMinValue === 'today') {
    return new Date();
  }
  return undefined;
};

export const CustomInput = ({
  ariaLabel,
  autoCorrect,
  availableImages,
  customInput,
  dateMinValue,
  description,
  diacritics,
  errorMessage,
  inputs,
  isDisabled,
  isReadonly,
  isRequired,
  items,
  label,
  maxSelection,
  minValue,
  name,
  onChange,
  onKeyDown,
  title,
  type,
  value,
  variant,
}: {
  ariaLabel?: string;
  autoCorrect?: string[];
  availableImages?: { name: string; link: string }[];
  customInput?: any;
  dateMinValue?: string;
  description?: string;
  diacritics?: string[];
  errorMessage?: string;
  inputs?: { type: string; name: string }[];
  isDisabled?: boolean;
  isReadonly?: boolean;
  isRequired?: boolean;
  items?: { id: string; label: string }[];
  label?: string;
  maxSelection?: number;
  minValue?: number;
  name: string;
  onChange?: (value: any) => void;
  onKeyDown?: (value: any) => void;
  title?: string;
  type?: string;
  value?: any[] | string;
  variant?: string;
}) => {
  switch (type) {
    case 'password':
      return (
        <PasswordInput
          description={description}
          error={errorMessage}
          required={isRequired}
          label={label}
          name={name}
          onKeyDown={onKeyDown}
          onChange={(event) => {
            onChange?.(event.currentTarget.value);
          }}
          value={value as string}
          disabled={isDisabled || isReadonly}
        />
      );
    case 'text':
      return (
        <TextInputWithDiacritics
          description={description}
          errorMessage={errorMessage}
          isRequired={isRequired}
          label={label}
          name={name}
          onKeyDown={onKeyDown}
          onChange={onChange}
          type={type}
          value={value as string}
          isDisabled={isDisabled}
          isReadonly={isReadonly}
          diacritics={diacritics}
        />
      );
    case 'tel':
    case 'email':
      return (
        <TextInput
          description={description}
          error={errorMessage}
          required={isRequired}
          label={label}
          name={name}
          onKeyDown={onKeyDown}
          onChange={(event) => {
            onChange?.(event.currentTarget.value);
          }}
          type={type}
          value={value as string}
          disabled={isDisabled}
          readOnly={isReadonly}
        />
      );
    case 'number':
      return (
        <NumberInput
          hideControls={true}
          aria-label={ariaLabel}
          description={description}
          error={errorMessage}
          required={isRequired}
          label={label}
          min={minValue}
          name={name}
          onChange={(value) => {
            onChange?.(value);
          }}
          value={value ? Number(value) : undefined}
          disabled={isDisabled}
          readOnly={isReadonly}
        />
      );
    case 'picker':
      return (
        <NativeSelect
          description={description}
          error={errorMessage}
          required={isRequired}
          label={label}
          name={name}
          onChange={(event) => {
            onChange?.(event.currentTarget.value);
          }}
          value={value as string}
          disabled={isDisabled || isReadonly}
          data={[
            { value: '', label: '-' },
            ...(items?.map((item) => ({
              value: item.id,
              label: item.label,
            })) || []),
          ]}
        />
      );
    case 'datepicker':
      return (
        <DatePickerInput
          label={label}
          minDate={getMinDate(dateMinValue)}
          name={name}
          description={description}
          onChange={(dateValue) => {
            if (!dateValue) {
              onChange?.(null);
              return;
            }

            // Convert to Date object if it isn't already
            const date = new Date(dateValue as any);
            if (!isNaN(date.getTime())) {
              onChange?.(date.toISOString());
            }
          }}
          value={value ? new Date(value as string) : null}
          disabled={isDisabled || isReadonly}
          valueFormat="YYYY-MM-DD"
          popoverProps={{ zIndex: 10000 }}
        />
      );
    case 'signature':
      return (
        <Signature
          onChange={(signaturePath: string) => {
            onChange?.(signaturePath);
          }}
          value={value as string}
          isDisabled={isDisabled || isReadonly}
        />
      );
    case 'multi-level-combobox':
      return (
        <MultiLevelCombobox
          isDisabled={isDisabled || isReadonly}
          customInput={customInput}
          value={value}
          onChange={(value: any) => onChange?.(value)}
        />
      );
    case 'heading':
      return <Title order={4}>{title}</Title>;
    case 'title':
      return <Title order={4}>{title}</Title>;
    case 'disclaimer':
      return (
        <Alert
          variant="light"
          color={getVariantColor(variant || '')}
          title={title}
        >
          {description}
        </Alert>
      );
    case 'divider':
      return <Divider />;
    case 'products':
      return (
        <Products
          isDisabled={isDisabled || isReadonly}
          name={name}
          label={label ? label : name}
          onChange={(value: any[]) => {
            onChange?.(value as any[]);
          }}
          value={value as any[]}
        />
      );
    case 'robeSizes':
      return (
        <RobeSizes
          isDisabled={isDisabled || isReadonly}
          values={value || {}}
          label={label}
          onSelectionChange={(value: any) => {
            onChange?.(value);
          }}
        />
      );
    case 'infinite-list':
      return (
        <InfiniteList
          diacritics={diacritics}
          autoCorrect={autoCorrect}
          isReadonly={isReadonly}
          label={label}
          inputs={inputs}
          value={value as any[]}
          onChange={(value: any) => onChange?.(value)}
          isDisabled={isDisabled}
        />
      );
    case 'textarea':
      return (
        <TextareaWithAutoCorrect
          description={description}
          errorMessage={errorMessage}
          isRequired={isRequired}
          label={label}
          name={name}
          onChange={onChange}
          value={value as string}
          isDisabled={isDisabled}
          isReadonly={isReadonly}
          autoCorrect={autoCorrect}
        />
      );
    case 'map':
      return (
        <>
          {label && (
            <Text size="sm" fw={500}>
              {label}
              {isRequired && <Text c="error"> *</Text>}
            </Text>
          )}
          {description && (
            <Text size="xs" c="dimmed" mb={5}>
              {description}
            </Text>
          )}
          <MapInput
            value={value as any}
            onChange={(coords) => onChange?.(coords)}
            isDisabled={isDisabled || isReadonly}
          />
        </>
      );
    case 'imagePicker':
      return (
        <ImagePicker
          label={label}
          availableImages={availableImages || []}
          maxSelection={maxSelection || 1}
          value={value as string[]}
          onChange={(selectedImages) => onChange?.(selectedImages)}
          isDisabled={isDisabled || isReadonly}
        />
      );
    case 'checkboxGroup':
      return (
        <CheckboxGroup
          label={label}
          items={items}
          description={description}
          value={value as string[]}
          onChange={(selectedIds) => onChange?.(selectedIds)}
          isDisabled={isDisabled || isReadonly}
          name={name}
        />
      );
    case 'radioButtonGroup':
      return (
        <RadioButtonGroup
          label={label}
          items={items}
          description={description}
          value={value as string}
          onChange={(selectedId) => onChange?.(selectedId)}
          isDisabled={isDisabled || isReadonly}
        />
      );
    default:
      return <>Not implemented</>;
  }
};
